import { useState, useEffect } from 'react'
import { inventoryApi } from '../services/api'
import Button from '../components/common/Button'
import toast from 'react-hot-toast'

// Above this quantity we don't render one input per unit — bulk parts (screws,
// connectors) shouldn't be serial tracked.
const SERIAL_TRACK_LIMIT = 100

// Why a unit left the shelf. The reference label changes meaning with the reason.
const REMOVAL_REASONS = [
    { value: 'INSTALLED', label: 'Installed on a job', reference: 'Job / work order #' },
    { value: 'SOLD', label: 'Sold', reference: 'Invoice / customer' },
    { value: 'RMA', label: 'RMA / warranty', reference: 'RMA #' },
    { value: 'TRANSFERRED', label: 'Moved to another location', reference: 'Where it went' },
    { value: 'SCRAPPED', label: 'Scrapped / failed', reference: 'Reference' },
    { value: 'OTHER', label: 'Other', reference: 'Reference' },
]

const EMPTY_FORM = {
    location: '',
    quantity: 0,
    partNumber: '',
    name: '',
    description: '',
    inventoryType: '',
    binBoxId: '',
    shelfId: '',
    series: '',
    revision: '',
    firmware: '',
    serialNumber: '',
    serialNumbers: [],
    trackSerials: false,
    notes: '',
}

/* ---------- serial helpers ---------- */

// Values people have typed to mean "no serial". These are filler, not serials,
// so they never become a tracked unit.
const PLACEHOLDER_SERIALS = new Set(['-', '--', 'N/A', 'NA', 'NONE', 'NULL', '.', 'TESTSERIAL'])

function isRealSerial(value) {
    const v = (value || '').trim().toUpperCase()
    return v.length > 0 && !PLACEHOLDER_SERIALS.has(v)
}

// Accepts an array, a JSON string, or a comma/semicolon/newline separated string.
function normalizeSerials(value) {
    if (Array.isArray(value)) {
        return value
            .map(v => (typeof v === 'object' && v !== null ? v.serialNumber : v))
            .map(s => (s ?? '').toString().trim())
    }
    if (typeof value === 'string' && value.trim()) {
        const raw = value.trim()
        if (raw.startsWith('[')) {
            try {
                const parsed = JSON.parse(raw)
                if (Array.isArray(parsed)) return normalizeSerials(parsed)
            } catch {
                // fall through to delimiter split
            }
        }
        return raw.split(/[,;\n]+/).map(s => s.trim()).filter(Boolean)
    }
    return []
}

// Pulls serials off an item, preferring the new array field, falling back to the
// legacy single serialNumber column.
function getSerials(item) {
    const fromArray = normalizeSerials(item.serialNumbers)
    const list = fromArray.length ? fromArray : normalizeSerials(item.serialNumber)
    return list.filter(isRealSerial)
}

// Grows/shrinks the serial list so it always has exactly `qty` slots.
function resizeSerials(list, qty) {
    const size = Math.max(0, Math.min(qty, SERIAL_TRACK_LIMIT))
    const next = list.slice(0, size)
    while (next.length < size) next.push('')
    return next
}

function filledSerials(list) {
    return list.map(s => (s || '').trim()).filter(Boolean)
}

function findDuplicates(list) {
    const seen = new Set()
    const dupes = new Set()
    filledSerials(list).forEach(s => {
        const key = s.toUpperCase()
        if (seen.has(key)) dupes.add(key)
        seen.add(key)
    })
    return dupes
}

function reasonLabel(value) {
    const match = REMOVAL_REASONS.find(r => r.value === value)
    return match ? match.label : (value || 'Removed')
}

function referenceLabel(value) {
    const match = REMOVAL_REASONS.find(r => r.value === value)
    return match ? match.reference : 'Reference'
}

function formatDate(value) {
    if (!value) return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return String(value)
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

// Only send fields the API knows about — never spread a whole row back at it.
function buildPayload(source, { quantity, serials, trackSerials }) {
    const cleaned = trackSerials ? serials.map(s => (s || '').trim()) : []
    return {
        location: source.location || '',
        quantity,
        partNumber: source.partNumber || '',
        name: source.name || '',
        description: source.description || '',
        inventoryType: source.inventoryType || '',
        binBoxId: source.binBoxId || '',
        shelfId: source.shelfId || '',
        series: source.series || '',
        revision: source.revision || '',
        firmware: source.firmware || '',
        notes: source.notes || '',
        serialNumbers: cleaned,
        // Legacy column kept in sync so old reports/exports still show something.
        serialNumber: trackSerials
            ? filledSerials(cleaned).join(', ')
            : (source.serialNumber || ''),
    }
}

export default function Inventory() {
    const [loading, setLoading] = useState(true)
    const [items, setItems] = useState([])
    const [search, setSearch] = useState('')
    const [showModal, setShowModal] = useState(false)
    const [editingItem, setEditingItem] = useState(null)
    const [form, setForm] = useState(EMPTY_FORM)
    const [saving, setSaving] = useState(false)
    const [expandedId, setExpandedId] = useState(null)

    // Quantity adjustment modal: { item, mode: 'add' | 'remove', ... }
    const [unitModal, setUnitModal] = useState(null)
    const [unitSaving, setUnitSaving] = useState(false)

    // Unit history modal: { item, loading, units: [] }
    const [historyModal, setHistoryModal] = useState(null)

    // "Where did this go?" results for a searched serial.
    const [serialHits, setSerialHits] = useState([])

    // Report building
    const [locations, setLocations] = useState([])
    const [locationFilter, setLocationFilter] = useState('')
    const [selectedIds, setSelectedIds] = useState([])
    const [reportModal, setReportModal] = useState(null)
    const [reporting, setReporting] = useState(false)

    useEffect(() => {
        loadInventory()
        inventoryApi.getLocations()
            .then(res => { if (res.success) setLocations(res.locations || []) })
            .catch(() => { /* filter just stays empty */ })
    }, [])

    // With a full-screen sheet open, iOS happily scrolls the list underneath it.
    const anyModalOpen = showModal || !!unitModal || !!historyModal || !!reportModal
    useEffect(() => {
        if (!anyModalOpen) return
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = previous }
    }, [anyModalOpen])

    async function loadInventory() {
        setLoading(true)
        try {
            const response = await inventoryApi.getAll()
            if (response.success) {
                setItems(response.items || [])
            } else {
                toast.error('Failed to load inventory')
            }
        } catch (error) {
            toast.error('Error loading inventory')
        }
        setLoading(false)
    }

    function replaceItem(updated) {
        if (!updated) return
        setItems(prev => prev.map(i => (i.id === updated.id ? { ...i, ...updated } : i)))
    }

    async function handleSearch() {
        setSerialHits([])
        if (!search.trim()) {
            loadInventory()
            return
        }
        setLoading(true)
        try {
            const response = await inventoryApi.search(search)
            if (response.success) {
                setItems(response.items || [])
            }
        } catch (error) {
            toast.error('Search failed')
        }
        // Separate lookup so a serial that's already gone still resolves.
        try {
            const hits = await inventoryApi.lookupSerial(search.trim())
            if (hits.success) setSerialHits(hits.matches || [])
        } catch (error) {
            // Lookup endpoint unavailable — normal search results still stand.
        }
        setLoading(false)
    }

    /* ---------- add / edit modal ---------- */

    function openAddModal() {
        setEditingItem(null)
        setForm(EMPTY_FORM)
        setShowModal(true)
    }

    function openEditModal(item) {
        const serials = getSerials(item)
        const quantity = item.quantity || 0
        const trackSerials = serials.length > 0
        setEditingItem(item)
        setForm({
            location: item.location || '',
            quantity,
            partNumber: item.partNumber || '',
            name: item.name || '',
            description: item.description || '',
            inventoryType: item.inventoryType || '',
            binBoxId: item.binBoxId || '',
            shelfId: item.shelfId || '',
            series: item.series || '',
            revision: item.revision || '',
            firmware: item.firmware || '',
            serialNumber: trackSerials ? '' : (item.serialNumber || ''),
            serialNumbers: trackSerials ? resizeSerials(serials, quantity) : [],
            trackSerials,
            notes: item.notes || '',
        })
        setShowModal(true)
    }

    function closeModal() {
        setShowModal(false)
        setEditingItem(null)
        setForm(EMPTY_FORM)
    }

    function handleFormChange(field, value) {
        setForm(prev => ({ ...prev, [field]: value }))
    }

    // Quantity and the serial slots move together.
    function handleFormQuantityChange(rawValue) {
        const quantity = Math.max(0, parseInt(rawValue) || 0)
        setForm(prev => {
            if (!prev.trackSerials) return { ...prev, quantity }
            const dropped = filledSerials(prev.serialNumbers.slice(quantity))
            if (dropped.length) {
                toast(`Lowering the count removes ${dropped.join(', ')} from this item`, { icon: '⚠️' })
            }
            return { ...prev, quantity, serialNumbers: resizeSerials(prev.serialNumbers, quantity) }
        })
    }

    function toggleTrackSerials(checked) {
        setForm(prev => {
            if (checked && prev.quantity > SERIAL_TRACK_LIMIT) {
                toast.error(`Serial tracking is capped at ${SERIAL_TRACK_LIMIT} units per item`)
                return prev
            }
            return {
                ...prev,
                trackSerials: checked,
                serialNumbers: checked ? resizeSerials(prev.serialNumbers, prev.quantity) : [],
            }
        })
    }

    function handleSerialChange(index, value) {
        setForm(prev => {
            const next = [...prev.serialNumbers]
            next[index] = value.toUpperCase()
            return { ...prev, serialNumbers: next }
        })
    }

    async function handleSave() {
        if (!form.description.trim()) {
            toast.error('Description is required')
            return
        }
        if (form.trackSerials) {
            const dupes = findDuplicates(form.serialNumbers)
            if (dupes.size) {
                toast.error(`Serial ${[...dupes][0]} is entered twice`)
                return
            }
        }

        setSaving(true)
        try {
            const payload = buildPayload(form, {
                quantity: form.quantity,
                serials: form.serialNumbers,
                trackSerials: form.trackSerials,
            })
            const response = editingItem
                ? await inventoryApi.update(editingItem.id, payload)
                : await inventoryApi.create(payload)

            if (response.success) {
                toast.success(editingItem ? 'Item updated' : 'Item created')
                closeModal()
                loadInventory()
            } else {
                toast.error(response.message || 'Failed to save')
            }
        } catch (error) {
            toast.error('Error saving item')
        }
        setSaving(false)
    }

    async function handleDelete(item) {
        if (!confirm(`Delete "${item.name || item.description}"?`)) return

        try {
            const response = await inventoryApi.delete(item.id)
            if (response.success) {
                toast.success('Item deleted')
                loadInventory()
            } else {
                toast.error('Failed to delete')
            }
        } catch (error) {
            toast.error('Error deleting item')
        }
    }

    /* ---------- +/- on the list ---------- */

    function openUnitModal(item, mode) {
        const serials = getSerials(item)

        // Nothing tracked and nothing to track: keep the old one-click behaviour.
        if (!serials.length) {
            adjustUntracked(item, mode === 'add' ? 1 : -1)
            return
        }

        if (mode === 'remove') {
            setUnitModal({
                item,
                mode,
                serials,
                selected: [],
                untracked: 0,
                reason: 'INSTALLED',
                reference: '',
                notes: '',
            })
        } else {
            setUnitModal({ item, mode, serials, newSerials: [''] })
        }
    }

    // Fast path for items without serial tracking.
    async function adjustUntracked(item, delta) {
        const newQty = Math.max(0, (item.quantity || 0) + delta)
        if (newQty === item.quantity) return
        try {
            const response = await inventoryApi.updateQuantity(item.id, newQty)
            if (response.success) {
                setItems(prev => prev.map(i => (i.id === item.id ? { ...i, quantity: newQty } : i)))
            } else {
                toast.error('Failed to update quantity')
            }
        } catch (error) {
            toast.error('Error updating quantity')
        }
    }

    function toggleSelectedSerial(serial) {
        setUnitModal(prev => {
            const selected = prev.selected.includes(serial)
                ? prev.selected.filter(s => s !== serial)
                : [...prev.selected, serial]
            return { ...prev, selected }
        })
    }

    function setNewSerial(index, value) {
        setUnitModal(prev => {
            const newSerials = [...prev.newSerials]
            newSerials[index] = value.toUpperCase()
            return { ...prev, newSerials }
        })
    }

    async function commitUnitChange() {
        const { item, mode } = unitModal
        setUnitSaving(true)
        try {
            let response
            if (mode === 'remove') {
                const count = unitModal.selected.length + unitModal.untracked
                if (count === 0) {
                    toast.error('Pick which unit left the shelf')
                    setUnitSaving(false)
                    return
                }
                response = await inventoryApi.removeUnits(item.id, {
                    serials: unitModal.selected,
                    untracked: unitModal.untracked,
                    reason: unitModal.reason,
                    reference: unitModal.reference.trim(),
                    notes: unitModal.notes.trim(),
                })
            } else {
                const added = unitModal.newSerials.map(s => (s || '').trim())
                const entered = added.filter(Boolean)
                const current = getSerials(item)
                const clash = entered.find(s => current.some(c => c.toUpperCase() === s.toUpperCase()))
                if (clash) {
                    toast.error(`${clash} is already on this item`)
                    setUnitSaving(false)
                    return
                }
                response = await inventoryApi.addUnits(item.id, {
                    serials: entered,
                    blanks: added.length - entered.length,
                })
            }

            if (response.success) {
                replaceItem(response.item)
                toast.success(mode === 'remove'
                    ? `Removed ${unitModal.selected.join(', ') || 'unit'}`
                    : 'Units added')
                setUnitModal(null)
            } else {
                toast.error(response.message || 'Failed to update quantity')
            }
        } catch (error) {
            toast.error('Error updating quantity')
        }
        setUnitSaving(false)
    }

    /* ---------- history ---------- */

    async function openHistory(item) {
        setHistoryModal({ item, loading: true, units: [] })
        try {
            const response = await inventoryApi.getUnits(item.id)
            if (response.success) {
                setHistoryModal({ item, loading: false, units: response.units || [] })
            } else {
                setHistoryModal({ item, loading: false, units: [], error: true })
            }
        } catch (error) {
            setHistoryModal({ item, loading: false, units: [], error: true })
        }
    }

    /* ---------- render ---------- */

    const formSerialDupes = form.trackSerials ? findDuplicates(form.serialNumbers) : new Set()

    const visibleItems = locationFilter
        ? items.filter(i => (i.location || '') === locationFilter)
        : items

    const allVisibleSelected = visibleItems.length > 0 &&
        visibleItems.every(i => selectedIds.includes(i.id))

    function toggleSelected(id) {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
    }

    function toggleSelectAll() {
        setSelectedIds(allVisibleSelected ? [] : visibleItems.map(i => i.id))
    }

    function openReport() {
        setReportModal({
            scope: selectedIds.length > 0 ? 'selected' : (locationFilter ? 'location' : 'all'),
            includeSerials: true,
            includeHistory: true,
            historyDays: 90,
        })
    }

    async function generateReport() {
        setReporting(true)
        try {
            const options = {
                itemIds: reportModal.scope === 'selected' ? selectedIds : null,
                location: reportModal.scope === 'location' ? locationFilter : null,
                includeSerials: reportModal.includeSerials,
                includeHistory: reportModal.includeHistory,
                historyDays: Number(reportModal.historyDays) || 90,
            }

            const { blob, filename } = await inventoryApi.downloadReport(options)

            // Hand the blob to the browser as a download. Works on iOS Safari too,
            // which ignores programmatic clicks on links without a real href.
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            setTimeout(() => URL.revokeObjectURL(url), 10000)

            toast.success('Report downloaded')
            setReportModal(null)
        } catch (error) {
            toast.error('Could not generate the report')
        }
        setReporting(false)
    }

    return (
        <div>
            {/* Header */}
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Inventory</h1>
                    <p className="text-blue-100 mt-1">
                        {visibleItems.length} items
                        {selectedIds.length > 0 && ` · ${selectedIds.length} selected`}
                    </p>
                </div>
                <Button variant="secondary" onClick={openAddModal}>
                    ➕ Add Item
                </Button>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                    <div className="flex-1 min-w-full sm:min-w-0">
                        <input
                            type="text"
                            placeholder="Search part#, name, shelf, bin, serial..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                            enterKeyHint="search"
                            autoCapitalize="characters"
                            autoCorrect="off"
                            autoComplete="off"
                            spellCheck={false}
                        />
                    </div>
                    <Button onClick={handleSearch}>Search</Button>
                    <Button variant="secondary" onClick={() => { setSearch(''); setSerialHits([]); loadInventory(); }}>
                        Clear
                    </Button>
                </div>

                <div className="flex gap-2 mt-3 items-center">
                    <select
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        className="flex-1 min-w-0 px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All locations</option>
                        {locations.map(loc => (
                            <option key={loc} value={loc}>{loc}</option>
                        ))}
                    </select>
                    <Button variant="secondary" onClick={openReport}>
                        📄 PDF
                    </Button>
                </div>

                {visibleItems.length > 0 && (
                    <label className="flex items-center gap-2 mt-3 text-sm text-gray-600 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            onChange={toggleSelectAll}
                            className="w-5 h-5 shrink-0"
                        />
                        {allVisibleSelected ? 'Clear selection' : 'Select all shown'}
                    </label>
                )}
            </div>

            {/* Serial lookup — answers "where did this one go?" */}
            {serialHits.length > 0 && (
                <div className="bg-white rounded-xl shadow-md p-4 mb-6 border-l-4 border-blue-500">
                    <h2 className="text-sm font-semibold text-gray-700 mb-3">Serial lookup</h2>
                    <div className="space-y-3">
                        {serialHits.map(hit => (
                            <div key={hit.unitId || `${hit.itemId}-${hit.serialNumber}`} className="text-sm">
                                <p className="font-mono font-semibold text-gray-800">{hit.serialNumber}</p>
                                <p className="text-gray-600">
                                    {hit.name || hit.description}
                                    {hit.partNumber && ` · ${hit.partNumber}`}
                                </p>
                                {hit.status === 'REMOVED' ? (
                                    <p className="text-red-600">
                                        Removed {formatDate(hit.removedAt)}
                                        {hit.removedBy && ` by ${hit.removedBy}`}
                                        {` · ${reasonLabel(hit.removedReason)}`}
                                        {hit.reference && ` · ${hit.reference}`}
                                    </p>
                                ) : (
                                    <p className="text-green-700">
                                        In stock
                                        {hit.location && ` at ${hit.location}`}
                                        {hit.shelfId && ` · shelf ${hit.shelfId}`}
                                        {hit.binBoxId && ` · bin ${hit.binBoxId}`}
                                    </p>
                                )}
                                {hit.removedNotes && <p className="text-gray-500">{hit.removedNotes}</p>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Inventory List */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-48">
                        <div className="spinner w-8 h-8"></div>
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <p className="text-4xl mb-2">📦</p>
                        <p>No items found</p>
                    </div>
                ) : (
                    <>
                        {/* Phones: one card per item. No horizontal scrolling, and
                            every control is a real thumb-sized target. */}
                        <ul className="md:hidden divide-y">
                            {visibleItems.map(item => {
                                const serials = getSerials(item)
                                const tracked = serials.length > 0
                                const untracked = Math.max(0, (item.quantity || 0) - serials.length)
                                const expanded = expandedId === item.id

                                return (
                                    <li key={item.id} className="p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(item.id)}
                                                onChange={() => toggleSelected(item.id)}
                                                className="w-5 h-5 mt-1 shrink-0"
                                                aria-label="Select for report"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <p className="font-semibold text-gray-900 break-words">
                                                    {item.name || item.description || 'Untitled item'}
                                                </p>
                                                {item.partNumber && (
                                                    <p className="text-sm text-gray-600 font-mono break-words">
                                                        {item.partNumber}
                                                    </p>
                                                )}
                                                {item.name && item.description && (
                                                    <p className="text-sm text-gray-500 break-words mt-0.5">
                                                        {item.description}
                                                    </p>
                                                )}
                                            </div>
                                            <span className="shrink-0 px-3 py-1 bg-gray-100 rounded-full text-sm font-semibold text-gray-800">
                                                {item.quantity}
                                            </span>
                                        </div>

                                        {(item.location || item.shelfId || item.binBoxId) && (
                                            <p className="mt-2 text-sm text-gray-600 break-words">
                                                {item.location && <span className="font-semibold text-gray-800">{item.location}</span>}
                                                {item.location && (item.shelfId || item.binBoxId) && ' · '}
                                                {item.shelfId && `Shelf ${item.shelfId}`}
                                                {item.shelfId && item.binBoxId && ' · '}
                                                {item.binBoxId && `Bin ${item.binBoxId}`}
                                            </p>
                                        )}

                                        {tracked && (
                                            <button
                                                onClick={() => setExpandedId(expanded ? null : item.id)}
                                                className="mt-2 min-h-[44px] -ml-1 px-1 text-sm text-blue-600 text-left"
                                            >
                                                {expanded ? '▾' : '▸'} {serials.length} serial{serials.length === 1 ? '' : 's'} on file
                                                {untracked > 0 && ` · ${untracked} without`}
                                            </button>
                                        )}

                                        {expanded && tracked && (
                                            <div className="mt-1 mb-2 flex flex-wrap gap-2">
                                                {serials.map((serial, i) => (
                                                    <span
                                                        key={`${serial}-${i}`}
                                                        className="px-2 py-1 bg-blue-50 border border-blue-100 rounded text-xs font-mono text-gray-700 break-all"
                                                    >
                                                        {serial}
                                                    </span>
                                                ))}
                                                {untracked > 0 && (
                                                    <span className="px-2 py-1 border border-dashed rounded text-xs text-gray-500">
                                                        {untracked} with no serial
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        <div className="mt-3 flex items-center gap-2">
                                            <button
                                                onClick={() => openUnitModal(item, 'remove')}
                                                className="flex-1 min-h-[48px] bg-red-50 text-red-700 rounded-lg font-bold text-xl active:bg-red-100"
                                                aria-label="Remove a unit"
                                            >
                                                −
                                            </button>
                                            <button
                                                onClick={() => openUnitModal(item, 'add')}
                                                className="flex-1 min-h-[48px] bg-green-50 text-green-700 rounded-lg font-bold text-xl active:bg-green-100"
                                                aria-label="Add a unit"
                                            >
                                                +
                                            </button>
                                            <button
                                                onClick={() => openHistory(item)}
                                                className="w-12 min-h-[48px] rounded-lg border text-lg active:bg-gray-100"
                                                aria-label="Unit history"
                                            >
                                                🕘
                                            </button>
                                            <button
                                                onClick={() => openEditModal(item)}
                                                className="w-12 min-h-[48px] rounded-lg border text-lg active:bg-gray-100"
                                                aria-label="Edit item"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item)}
                                                className="w-12 min-h-[48px] rounded-lg border text-lg active:bg-gray-100"
                                                aria-label="Delete item"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>

                        {/* Tablet and up: the table stays. */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-3 w-10"></th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Qty</th>
                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Part #</th>
                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location</th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {visibleItems.map(item => {
                                        const serials = getSerials(item)
                                        const tracked = serials.length > 0
                                        const untracked = Math.max(0, (item.quantity || 0) - serials.length)
                                        const expanded = expandedId === item.id

                                        return [
                                            <tr key={item.id} className="hover:bg-gray-50">
                                                <td className="px-3 py-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.includes(item.id)}
                                                        onChange={() => toggleSelected(item.id)}
                                                        className="w-4 h-4"
                                                        aria-label="Select for report"
                                                    />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-center gap-1">
                                                        <button
                                                            onClick={() => openUnitModal(item, 'remove')}
                                                            className="w-8 h-8 bg-red-100 text-red-600 rounded hover:bg-red-200 font-bold"
                                                            title={tracked ? 'Remove a unit (choose serial)' : 'Remove one'}
                                                        >
                                                            -
                                                        </button>
                                                        <span className="w-10 text-center font-semibold">{item.quantity}</span>
                                                        <button
                                                            onClick={() => openUnitModal(item, 'add')}
                                                            className="w-8 h-8 bg-green-100 text-green-600 rounded hover:bg-green-200 font-bold"
                                                            title={tracked ? 'Add a unit (enter serial)' : 'Add one'}
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600">{item.partNumber || '-'}</td>
                                                <td className="px-4 py-3">
                                                    <p className="font-medium text-gray-800">{item.name || '-'}</p>
                                                    {item.inventoryType && (
                                                        <span className="text-xs text-gray-500">{item.inventoryType}</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600">
                                                    <p>{item.description || '-'}</p>
                                                    {tracked && (
                                                        <button
                                                            onClick={() => setExpandedId(expanded ? null : item.id)}
                                                            className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                                                        >
                                                            {expanded ? '▾' : '▸'} {serials.length} serial{serials.length === 1 ? '' : 's'} on file
                                                            {untracked > 0 && ` · ${untracked} without a serial`}
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <div>
                                                        {item.location && (
                                                            <p className="font-bold text-gray-800">{item.location}</p>
                                                        )}
                                                        {item.shelfId && <span className="text-gray-600">Shelf: {item.shelfId}</span>}
                                                        {item.shelfId && item.binBoxId && <br />}
                                                        {item.binBoxId && <span className="text-gray-500">Bin: {item.binBoxId}</span>}
                                                        {!item.location && !item.shelfId && !item.binBoxId && '-'}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={() => openHistory(item)}
                                                            className="text-gray-500 hover:text-gray-800"
                                                            title="Unit history"
                                                        >
                                                            🕘
                                                        </button>
                                                        <button
                                                            onClick={() => openEditModal(item)}
                                                            className="text-blue-600 hover:text-blue-800"
                                                            title="Edit"
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(item)}
                                                            className="text-red-600 hover:text-red-800"
                                                            title="Delete"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>,
                                            expanded && (
                                                <tr key={`${item.id}-serials`} className="bg-blue-50">
                                                    <td colSpan={7} className="px-4 py-3">
                                                        <div className="flex flex-wrap gap-2">
                                                            {serials.map((serial, i) => (
                                                                <span
                                                                    key={`${serial}-${i}`}
                                                                    className="px-2 py-1 bg-white border rounded text-xs font-mono text-gray-700"
                                                                >
                                                                    {serial}
                                                                </span>
                                                            ))}
                                                            {untracked > 0 && (
                                                                <span className="px-2 py-1 border border-dashed rounded text-xs text-gray-500">
                                                                    {untracked} unit{untracked === 1 ? '' : 's'} with no serial recorded
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ),
                                        ]
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center sm:justify-center sm:p-4">
                    <div className="bg-white w-full sm:max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[90vh] sm:rounded-xl shadow-xl flex flex-col">
                        <div className="p-4 sm:p-6 border-b shrink-0 pt-[max(1rem,env(safe-area-inset-top))] sm:pt-6">
                            <h2 className="text-xl font-semibold">
                                {editingItem ? 'Edit Item' : 'Add New Item'}
                            </h2>
                        </div>

                        <div className="p-4 sm:p-6 space-y-4 overflow-y-auto grow overscroll-contain">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                                    <input
                                        type="text"
                                        value={form.location}
                                        onChange={(e) => handleFormChange('location', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={45}
                                        placeholder="e.g., SHOP, STORAGE UNIT"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                                    <input
                                        type="number"
                                        value={form.quantity}
                                        onChange={(e) => handleFormQuantityChange(e.target.value)}
                                        className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Part Number</label>
                                    <input
                                        type="text"
                                        value={form.partNumber}
                                        onChange={(e) => handleFormChange('partNumber', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={(e) => handleFormChange('name', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                                <input
                                    type="text"
                                    value={form.description}
                                    onChange={(e) => handleFormChange('description', e.target.value.toUpperCase())}
                                    className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    maxLength={240}
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                    <input
                                        type="text"
                                        value={form.inventoryType}
                                        onChange={(e) => handleFormChange('inventoryType', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Shelf</label>
                                    <input
                                        type="text"
                                        value={form.shelfId}
                                        onChange={(e) => handleFormChange('shelfId', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                            </div>

                            {/* Serial numbers */}
                            <div className="border rounded-lg p-4 bg-gray-50">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.trackSerials}
                                        onChange={(e) => toggleTrackSerials(e.target.checked)}
                                        className="w-5 h-5 shrink-0"
                                    />
                                    <span className="text-sm font-medium text-gray-700">
                                        Track each unit by serial number
                                    </span>
                                </label>

                                {!form.trackSerials ? (
                                    <div className="mt-3">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                                        <input
                                            type="text"
                                            value={form.serialNumber}
                                            onChange={(e) => handleFormChange('serialNumber', e.target.value.toUpperCase())}
                                            className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                            maxLength={40}
                                        />
                                    </div>
                                ) : form.quantity === 0 ? (
                                    <p className="mt-3 text-sm text-gray-500">
                                        Set a quantity above and a serial field appears for each unit.
                                    </p>
                                ) : (
                                    <div className="mt-3 space-y-2">
                                        <p className="text-xs text-gray-500">
                                            One field per unit. Leave a field blank if you don't have that serial yet.
                                        </p>
                                        <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                                            {form.serialNumbers.map((serial, index) => {
                                                const isDupe = serial.trim() && formSerialDupes.has(serial.trim().toUpperCase())
                                                return (
                                                    <div key={index} className="flex items-center gap-2">
                                                        <span className="w-14 text-xs text-gray-500 shrink-0">Unit {index + 1}</span>
                                                        <input
                                                            type="text"
                                                            value={serial}
                                                            onChange={(e) => handleSerialChange(index, e.target.value)}
                                                            className={`flex-1 px-3 py-2 border rounded-lg font-mono text-base focus:ring-2 focus:ring-blue-500 ${isDupe ? 'border-red-400 bg-red-50' : ''}`}
                                                            maxLength={40}
                                                            placeholder="Serial number"
                                                            autoCapitalize="characters"
                                                            autoCorrect="off"
                                                            autoComplete="off"
                                                            spellCheck={false}
                                                            enterKeyHint="next"
                                                        />
                                                    </div>
                                                )
                                            })}
                                        </div>
                                        {formSerialDupes.size > 0 && (
                                            <p className="text-xs text-red-600">
                                                Two units share the same serial. Each one has to be unique.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Bin/Box</label>
                                <input
                                    type="text"
                                    value={form.binBoxId}
                                    onChange={(e) => handleFormChange('binBoxId', e.target.value.toUpperCase())}
                                    className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    maxLength={40}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Series</label>
                                    <input
                                        type="text"
                                        value={form.series}
                                        onChange={(e) => handleFormChange('series', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Revision</label>
                                    <input
                                        type="text"
                                        value={form.revision}
                                        onChange={(e) => handleFormChange('revision', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Firmware</label>
                                    <input
                                        type="text"
                                        value={form.firmware}
                                        onChange={(e) => handleFormChange('firmware', e.target.value.toUpperCase())}
                                        className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        maxLength={40}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <input
                                    type="text"
                                    value={form.notes}
                                    onChange={(e) => handleFormChange('notes', e.target.value.toUpperCase())}
                                    className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    maxLength={100}
                                />
                            </div>
                        </div>

                        <div className="p-4 sm:p-6 border-t flex justify-end gap-3 shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-6">
                            <Button variant="secondary" onClick={closeModal}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Which unit? modal */}
            {unitModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center sm:justify-center sm:p-4">
                    <div className="bg-white w-full sm:max-w-md h-[100dvh] sm:h-auto sm:max-h-[85vh] sm:rounded-xl shadow-xl flex flex-col">
                        <div className="p-4 sm:p-5 border-b shrink-0 pt-[max(1rem,env(safe-area-inset-top))] sm:pt-5">
                            <h2 className="text-lg font-semibold">
                                {unitModal.mode === 'remove' ? 'Which unit is leaving?' : 'Add units'}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                {unitModal.item.name || unitModal.item.description}
                            </p>
                        </div>

                        <div className="p-4 sm:p-5 space-y-2 overflow-y-auto grow overscroll-contain">
                            {unitModal.mode === 'remove' ? (
                                <>
                                    {unitModal.serials.map(serial => (
                                        <label
                                            key={serial}
                                            className="flex items-center gap-3 px-3 min-h-[52px] border rounded-lg cursor-pointer hover:bg-gray-50 active:bg-gray-100"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={unitModal.selected.includes(serial)}
                                                onChange={() => toggleSelectedSerial(serial)}
                                                className="w-5 h-5 shrink-0"
                                            />
                                            <span className="font-mono text-base text-gray-800 break-all">{serial}</span>
                                        </label>
                                    ))}

                                    {unitModal.item.quantity > unitModal.serials.length && (
                                        <label className="flex items-center gap-3 px-3 min-h-[52px] border border-dashed rounded-lg cursor-pointer hover:bg-gray-50 active:bg-gray-100">
                                            <input
                                                type="checkbox"
                                                checked={unitModal.untracked > 0}
                                                onChange={(e) => setUnitModal(prev => ({
                                                    ...prev,
                                                    untracked: e.target.checked ? 1 : 0,
                                                }))}
                                                className="w-5 h-5 shrink-0"
                                            />
                                            <span className="text-sm text-gray-600">
                                                A unit with no serial on file
                                            </span>
                                        </label>
                                    )}

                                    <div className="pt-3 space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Where did it go?
                                            </label>
                                            <select
                                                value={unitModal.reason}
                                                onChange={(e) => setUnitModal(prev => ({ ...prev, reason: e.target.value }))}
                                                className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                            >
                                                {REMOVAL_REASONS.map(r => (
                                                    <option key={r.value} value={r.value}>{r.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {referenceLabel(unitModal.reason)}
                                            </label>
                                            <input
                                                type="text"
                                                value={unitModal.reference}
                                                onChange={(e) => setUnitModal(prev => ({ ...prev, reference: e.target.value.toUpperCase() }))}
                                                className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                                maxLength={60}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                            <input
                                                type="text"
                                                value={unitModal.notes}
                                                onChange={(e) => setUnitModal(prev => ({ ...prev, notes: e.target.value.toUpperCase() }))}
                                                className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                                maxLength={240}
                                            />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="text-xs text-gray-500">
                                        Enter the serial for each unit coming in. Blank still raises the count.
                                    </p>
                                    {unitModal.newSerials.map((serial, index) => (
                                        <input
                                            key={index}
                                            type="text"
                                            value={serial}
                                            onChange={(e) => setNewSerial(index, e.target.value)}
                                            className="w-full px-3 py-2 border rounded-lg font-mono text-base focus:ring-2 focus:ring-blue-500"
                                            maxLength={40}
                                            placeholder={`New unit ${index + 1} serial`}
                                            autoCapitalize="characters"
                                            autoCorrect="off"
                                            autoComplete="off"
                                            spellCheck={false}
                                            enterKeyHint="next"
                                        />
                                    ))}
                                    <button
                                        onClick={() => setUnitModal(prev => ({ ...prev, newSerials: [...prev.newSerials, ''] }))}
                                        className="text-sm text-blue-600 hover:text-blue-800"
                                    >
                                        + Another unit
                                    </button>
                                </>
                            )}
                        </div>

                        <div className="p-4 sm:p-5 border-t flex justify-end gap-3 shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-5">
                            <Button variant="secondary" onClick={() => setUnitModal(null)}>
                                Cancel
                            </Button>
                            <Button onClick={commitUnitChange} disabled={unitSaving}>
                                {unitSaving
                                    ? 'Saving...'
                                    : unitModal.mode === 'remove'
                                        ? `Remove ${unitModal.selected.length + unitModal.untracked || ''}`.trim()
                                        : 'Add units'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Report options */}
            {reportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center sm:justify-center sm:p-4">
                    <div className="bg-white w-full sm:max-w-md h-[100dvh] sm:h-auto sm:max-h-[85vh] sm:rounded-xl shadow-xl flex flex-col">
                        <div className="p-4 sm:p-5 border-b shrink-0 pt-[max(1rem,env(safe-area-inset-top))] sm:pt-5">
                            <h2 className="text-lg font-semibold">Inventory PDF</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Grouped by location, with a box to tick off each serial.
                            </p>
                        </div>

                        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto grow overscroll-contain">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">What to include</label>
                                <div className="space-y-2">
                                    <label className={`flex items-center gap-3 px-3 min-h-[52px] border rounded-lg cursor-pointer ${selectedIds.length === 0 ? 'opacity-50' : ''}`}>
                                        <input
                                            type="radio"
                                            checked={reportModal.scope === 'selected'}
                                            disabled={selectedIds.length === 0}
                                            onChange={() => setReportModal(prev => ({ ...prev, scope: 'selected' }))}
                                            className="w-5 h-5 shrink-0"
                                        />
                                        <span className="text-sm">
                                            Selected items
                                            {selectedIds.length > 0 && ` (${selectedIds.length})`}
                                        </span>
                                    </label>

                                    <label className={`flex items-center gap-3 px-3 min-h-[52px] border rounded-lg cursor-pointer ${!locationFilter ? 'opacity-50' : ''}`}>
                                        <input
                                            type="radio"
                                            checked={reportModal.scope === 'location'}
                                            disabled={!locationFilter}
                                            onChange={() => setReportModal(prev => ({ ...prev, scope: 'location' }))}
                                            className="w-5 h-5 shrink-0"
                                        />
                                        <span className="text-sm">
                                            {locationFilter || 'Pick a location first'}
                                        </span>
                                    </label>

                                    <label className="flex items-center gap-3 px-3 min-h-[52px] border rounded-lg cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={reportModal.scope === 'all'}
                                            onChange={() => setReportModal(prev => ({ ...prev, scope: 'all' }))}
                                            className="w-5 h-5 shrink-0"
                                        />
                                        <span className="text-sm">Everything ({items.length} items)</span>
                                    </label>
                                </div>
                            </div>

                            <label className="flex items-center gap-3 px-3 min-h-[52px] border rounded-lg cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={reportModal.includeSerials}
                                    onChange={(e) => setReportModal(prev => ({ ...prev, includeSerials: e.target.checked }))}
                                    className="w-5 h-5 shrink-0"
                                />
                                <span className="text-sm">List every serial number</span>
                            </label>

                            <label className="flex items-center gap-3 px-3 min-h-[52px] border rounded-lg cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={reportModal.includeHistory}
                                    onChange={(e) => setReportModal(prev => ({ ...prev, includeHistory: e.target.checked }))}
                                    className="w-5 h-5 shrink-0"
                                />
                                <span className="text-sm">Add the removal log</span>
                            </label>

                            {reportModal.includeHistory && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Removals going back
                                    </label>
                                    <select
                                        value={reportModal.historyDays}
                                        onChange={(e) => setReportModal(prev => ({ ...prev, historyDays: e.target.value }))}
                                        className="w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value={30}>30 days</option>
                                        <option value={90}>90 days</option>
                                        <option value={365}>1 year</option>
                                        <option value={3650}>Everything</option>
                                    </select>
                                </div>
                            )}

                            <p className="text-xs text-gray-500">
                                A full run of {items.length} items takes a few seconds and prints to
                                roughly {Math.max(1, Math.ceil(items.length / 22))} pages.
                            </p>
                        </div>

                        <div className="p-4 sm:p-5 border-t flex justify-end gap-3 shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-5">
                            <Button variant="secondary" onClick={() => setReportModal(null)}>
                                Cancel
                            </Button>
                            <Button onClick={generateReport} disabled={reporting}>
                                {reporting ? 'Building...' : 'Download PDF'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Unit history modal */}
            {historyModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center sm:justify-center sm:p-4">
                    <div className="bg-white w-full sm:max-w-lg h-[100dvh] sm:h-auto sm:max-h-[85vh] sm:rounded-xl shadow-xl flex flex-col">
                        <div className="p-4 sm:p-5 border-b shrink-0 pt-[max(1rem,env(safe-area-inset-top))] sm:pt-5">
                            <h2 className="text-lg font-semibold">Unit history</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                {historyModal.item.name || historyModal.item.description}
                            </p>
                        </div>

                        <div className="p-4 sm:p-5 overflow-y-auto grow overscroll-contain">
                            {historyModal.loading ? (
                                <div className="flex items-center justify-center h-24">
                                    <div className="spinner w-6 h-6"></div>
                                </div>
                            ) : historyModal.error ? (
                                <p className="text-sm text-gray-500">
                                    History couldn't be loaded. Check that the unit endpoints are running.
                                </p>
                            ) : historyModal.units.length === 0 ? (
                                <p className="text-sm text-gray-500">
                                    No units recorded yet. Turn on serial tracking in Edit to start a history.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {historyModal.units.map(unit => (
                                        <div
                                            key={unit.id}
                                            className={`px-3 py-2 border rounded-lg ${unit.status === 'REMOVED' ? 'bg-gray-50' : 'bg-white'}`}
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="font-mono text-sm text-gray-800">
                                                    {unit.serialNumber || 'No serial recorded'}
                                                </span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${unit.status === 'REMOVED' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                                    {unit.status === 'REMOVED' ? 'Removed' : 'In stock'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Added {formatDate(unit.addedAt)}
                                                {unit.addedBy && ` by ${unit.addedBy}`}
                                            </p>
                                            {unit.status === 'REMOVED' && (
                                                <p className="text-xs text-gray-600">
                                                    {reasonLabel(unit.removedReason)} on {formatDate(unit.removedAt)}
                                                    {unit.removedBy && ` by ${unit.removedBy}`}
                                                    {unit.reference && ` · ${unit.reference}`}
                                                </p>
                                            )}
                                            {unit.removedNotes && (
                                                <p className="text-xs text-gray-500">{unit.removedNotes}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="p-4 sm:p-5 border-t flex justify-end shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-5">
                            <Button variant="secondary" onClick={() => setHistoryModal(null)}>
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}